import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Add-to-cart confirmation layer. Opens after every add-to-cart (PDP,
 * sticky bar, quick-add, or its own cross-sell), shows a recap of the
 * added line, and stays open until manually dismissed.
 */
class CartConfirmationComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
  }

  /**
   * @param {Event} event
   */
  #onCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;
    if (event.detail?.data?.source !== 'product-form-component') return;

    const addedItemKey = /** @type {{key?: string}} */ (event.detail?.resource)?.key;
    const sectionsHtml = event.detail?.data?.sections;

    /** @type {DialogComponent | null} */
    const quickAdd = /** @type {DialogComponent | null} */ (document.getElementById('quick-add-drawer'));
    const quickAddDialog = quickAdd?.refs?.dialog;
    const isSwapping = quickAddDialog instanceof HTMLDialogElement && quickAddDialog.open;

    if (isSwapping && quickAdd) {
      this.#openWithSwap(quickAdd, quickAddDialog, sectionsHtml, addedItemKey);
    } else {
      this.#openDirect(sectionsHtml, addedItemKey);
    }
  };

  /**
   * Opens the confirmation layer by swapping from the quick-add drawer with a view transition.
   * @param {DialogComponent} quickAdd
   * @param {HTMLDialogElement} quickAddDialog
   * @param {Record<string, string> | undefined} sectionsHtml
   * @param {string | undefined} addedItemKey
   */
  #openWithSwap(quickAdd, quickAddDialog, sectionsHtml, addedItemKey) {
    /** @type {HTMLDialogElement} */
    const dialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);

    quickAddDialog.style.viewTransitionName = 'drawer-shell';

    if (!document.startViewTransition) {
      this.#morphAndReveal(sectionsHtml, addedItemKey);
      quickAdd.closeDialog({ immediate: true });
      this.showDialog({ immediate: true });
      this.#focusHeader();
      this.#resetScrollArea();
      return;
    }

    const transition = document.startViewTransition(() => {
      quickAddDialog.style.viewTransitionName = '';
      this.#morphAndReveal(sectionsHtml, addedItemKey);
      quickAdd.closeDialog({ immediate: true });
      this.showDialog({ immediate: true });
      this.#focusHeader();
      this.#resetScrollArea();
      dialog.style.viewTransitionName = 'drawer-shell';
    });

    transition.finished.then(() => {
      this.#focusHeader();
      this.#resetScrollArea();
      dialog.style.viewTransitionName = '';
    });
  }

  /**
   * Opens the confirmation layer directly (no quick-add swap).
   * @param {Record<string, string> | undefined} sectionsHtml
   * @param {string | undefined} addedItemKey
   */
  #openDirect(sectionsHtml, addedItemKey) {
    this.#morphAndReveal(sectionsHtml, addedItemKey);
    this.showDialog();
    this.#focusHeader();
    this.#resetScrollArea();
  }

  /**
   * Morphs the confirmation section with fresh HTML, then reveals the item
   * matching the added line's key (hides every other rendered line).
   * @param {Record<string, string> | undefined} sectionsHtml
   * @param {string | undefined} addedItemKey
   */
  #morphAndReveal(sectionsHtml, addedItemKey) {
    const sectionId = this.dataset.sectionId;
    if (sectionId && sectionsHtml?.[sectionId]) {
      const wasOpen = this.refs.dialog.open;
      morphSection(sectionId, sectionsHtml[sectionId], 'hydration');
      if (wasOpen) {
        this.refs.dialog.setAttribute('scroll-lock', '');
      }
    }

    if (!addedItemKey) return;

    for (const item of this.refs.dialog.querySelectorAll('.cart-confirmation-item')) {
      item.classList.toggle('cart-confirmation-item--active', item.getAttribute('data-key') === addedItemKey);
    }
  }

  #resetScrollArea() {
    const scrollArea = this.refs.scrollArea;
    if (!(scrollArea instanceof HTMLElement)) return;

    scrollArea.scrollTop = 0;

    requestAnimationFrame(() => {
      scrollArea.scrollTop = 0;
    });
  }

  #focusHeader() {
    const title = this.refs.dialog.querySelector('.dialog-title');

    if (title instanceof HTMLElement) {
      title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
      return;
    }

    const closeButton = this.refs.dialog.querySelector('.icon-button--close');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus({ preventScroll: true });
    }
  }

  open = () => {
    this.showDialog();
  };

  close = () => {
    this.closeDialog();
  };
}

if (!customElements.get('cart-confirmation-component')) {
  customElements.define('cart-confirmation-component', CartConfirmationComponent);
}
